<template>
    <div class="CFavouriteButton">
        <CButton
            class="disabled:!bg-transparent !text-gray-600 dark:!text-gray-400 hover:!text-gray-900 dark:hover:!text-gray-200 !no-underline hover:!no-underline"
            :disabled="loading || preview"
            :event="preview ? '' : 'click'"
            :icon="isFavourite ? 'star' : 'star-outline'"
            :loading="loading"
            size="small"
            :variant="isFavourite ? 'favourite' : 'text'"
            @click="!preview ? toggle() : undefined"
        >
            <span class="!no-underline" :class="{ 'hidden sm:block': hideTextOnMobile }">{{
                isFavourite ? $t('favorites.saveButton.saved', lang) : $t('favorites.saveButton.save', lang)
            }}</span>
        </CButton>
    </div>
</template>

<script setup lang="ts">
import { useApplicationStore } from '@/stores/application'
import { useModalStore } from '@/stores/modal'
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'

import { OrgType } from '#shared/types/Org'
import SignInModal from '@/components/modals/SignInModal.vue'
import { useExponea } from '@/composables/exponea/useExponea'
import { getFullPathWithDomain } from '@/utils/helpers'

interface Favorites {
    skill: string[]
    mentor: string[]
    project: string[]
    volunteer: string[]
    job: string[]
    org: string[]
    blog: string[]
    [key: string]: string[]
}

const props = defineProps({
    type: {
        type: String,
        required: true
    },
    title: {
        type: String,
        required: true
    },
    id: {
        type: String,
        default: undefined
    },
    orgName: {
        type: String,
        default: ''
    },
    orgType: {
        type: String as () => OrgType,
        default: undefined
    },
    lang: {
        type: String,
        default: 'sk'
    },
    preview: {
        type: Boolean,
        default: false
    },
    hideTextOnMobile: {
        type: Boolean,
        default: false
    }
})

const loading = ref(false)
const store = useApplicationStore()
const modalStore = useModalStore()
const route = useRoute()
const { $exponea: exponea } = useNuxtApp()
const { trackExponeaEvent } = useExponea()

const isFavourite = computed((): boolean => {
    return !props.preview ? (store.userFavorites as Favorites)[props.type]?.includes(props.id) : false
})

const loggedIn = computed((): boolean => {
    return !props.preview ? store.isLoggedIn : false
})

function openLogInModal() {
    exponea?.track('cta1_favourites_not_loggedin', {
        opp_type: props.type,
        opp_name: props.title,
        opp_organisation: props.orgName,
        opp_id: props.id,
        opp_url: getFullPathWithDomain(route.fullPath),
        org_type: props.orgType
    })

    if (process.client) {
        localStorage.setItem('beforeLogInLocation', route.fullPath)
    }

    modalStore.openModal(SignInModal, {
        message: `Aby sme ti vedeli zobraziť, ktoré príležitosti si si uložil do svojho profilu,
        <strong>prihlás sa</strong>.`
    })
}

function toggle() {
    if (loggedIn.value) {
        trackExponeaEvent({
            eventName: 'cta1_favourites',
            userId: store.userId || '',
            eventProperties: {
                button_action: isFavourite.value ? 'removed' : 'added',
                opp_type: props.type,
                opp_name: props.title,
                opp_organisation: props.orgName || '',
                opp_id: props.id || '',
                opp_url: getFullPathWithDomain(route.fullPath),
                org_type: props.orgType || ''
            }
        })

        loading.value = true
        setTimeout(() => {
            loading.value = false

            // Add to favorites using a cleaner approach
            const favorites = store.userFavorites as Favorites
            if (!favorites[props.type]) {
                favorites[props.type] = []
            }

            const index = favorites[props.type].indexOf(props.id)
            if (index === -1) {
                favorites[props.type].push(props.id)
            } else {
                favorites[props.type].splice(index, 1)
            }

            // Update favorites in store and backend
            store.updateUserFavorites()
        }, 400)
    } else {
        openLogInModal()
    }
}
</script>
